<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $t('messages.groups'),
                        href: meta.links.group_index,
                    },
                    {
                        name: student.group.name,
                        href: meta.links.group_show,
                    },
                    {
                        name: student.full_name,
                        href: meta.links.show,
                    },
                    {
                        name: $tc('messages.adaptations', 2),
                        href: null,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div>
                        <ActivityMenuOptions>
                            <InertiaLink
                                v-for="otherStudent in meta.links.group.students"
                                :href="otherStudent.url"
                                :key="otherStudent.url"
                                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                            >
                                {{ otherStudent.text }}
                            </InertiaLink>
                        </ActivityMenuOptions>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>

        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <div class="flex flex-col mt-5">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $tc('messages.subject', 1) }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.type') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.adaptation_degree') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        <span class="sr-only">{{ $t('messages.actions') }}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="bg-white" v-for="(row, index) in form.adaptations" :key="index">
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <FormSelect
                                            id="subject_id"
                                            required
                                            v-model="row.subject_id"
                                            :options="options.subjects"
                                            class="mt-1 w-full"
                                        />
                                        <FormError
                                            :value="form.errors[`adaptations.${index}.subject_id`]"
                                            class="mt-2"
                                        />
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <FormSelect
                                            id="select_id"
                                            required
                                            v-model="row.select_id"
                                            :options="options.selects"
                                            class="mt-1 w-full"
                                        />
                                        <FormError
                                            :value="form.errors[`adaptations.${index}.select_id`]"
                                            class="mt-2"
                                        />
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <FormSelect
                                            id="degree"
                                            required
                                            v-model="row.degree"
                                            :options="options.degrees"
                                            class="mt-1 w-full"
                                        />
                                        <FormError :value="form.errors[`adaptations.${index}.degree`]" class="mt-2" />
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <button
                                            type="button"
                                            @click="deleteRow(index)"
                                            class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                    clip-rule="evenodd"
                                                ></path>
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="mt-3">
                        <button
                            type="button"
                            @click="addRow()"
                            class="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            <svg
                                class="h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <div class="flex justify-end">
                <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                    {{ $t('messages.save') }}
                    <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </div>
        </form>

        <div v-if="lastChanges" class="mt-5">
            {{ $tc('messages.last_changes', 1) }}

            <div class="flex flex-col mt-5">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.description') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.before') }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.after') }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                    v-for="(change, index) in lastChanges.changesTable"
                                >
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        {{ change.attribute }}
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <span v-if="change.old === '::no_info::'" class="text-gray-400">
                                            {{ $t('messages.no_info') }}
                                        </span>
                                        <span v-else-if="change.type === 'text'">
                                            <span v-html="change.old" class="" />
                                        </span>
                                        <span v-else>
                                            {{ change.old }}
                                        </span>
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <span v-if="change.new === '::no_info::'" class="text-gray-400">
                                            {{ $t('messages.no_info') }}
                                        </span>
                                        <span v-else-if="change.type === 'text'">
                                            <span v-html="change.new" class="" />
                                        </span>
                                        <span v-else>
                                            {{ change.new }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="text-sm mt-3">
                {{ $t('messages.causer') }} : {{ lastChanges.causer.full_name }}

                <span class="text-gray-400"> ({{ lastChanges.updated_at_display }})</span>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import AppButton from '@/components/AppButton.vue';
import ActivityMenuOptions from '@/components/pageSpecific/ActivityMenuOptions.vue';

export default {
    components: {
        PanelPage,
        FormError,
        FormSelect,
        AppButton,
        ActivityMenuOptions,
    },
    layout: Layout,
    name: 'Edit',
    props: ['student', 'meta', 'options', 'defaults', 'lastChanges'],
    data() {
        return {
            form: this.$inertia.form({
                adaptations: this.student.adaptations,
            }),
        };
    },
    methods: {
        addRow() {
            this.form.adaptations.push({
                subject_id: null,
                select_id: null,
                degree: this.defaults.degree,
            });
        },
        deleteRow(index) {
            this.form.adaptations.splice(index, 1);
        },
        submit() {
            this.form.patch(this.meta.links.update);
        },
    },
};
</script>
